import SynthEnvelope from '../SynthEnvelope.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import Effects from 'effects/Effects.js';

/**
 * mixin handling the Filters (Low pass and High pass) AND
 * the filter envelopes for the entire synth
 */

export default superclass => class extends superclass  {
    setupFilters() {
        // filter module
        this.filter = new Effects.FilterEffect(this.sequencerEngine);
        this.hpFilter = new Effects.FilterEffect(this.sequencerEngine);
        this.hpFilter.hipass();
        this.filter.connect(this.hpFilter.effectNode);

        this.filterEnvelope = new SynthEnvelope(
            this.sequencerEngine, this.mixSession, this.context,
            this.filter.effectNode.frequency, 0, 10000);

        this.filterEnvelope.name = "Filter Envelope (LP)";

        this.filterEnvelope.attackCurve = 'linear';
        this.filterEnvelope.decayCurve = 'linear';
        this.filterEnvelope.releaseCurve = 'linear';

        this.hpFilterEnvelope = new SynthEnvelope(
            this.sequencerEngine, this.mixSession, this.context,
            this.hpFilter.effectNode.frequency, 0, 10000);

        this.hpFilterEnvelope.name = "Filter Envelope (HP)";
        
        this.hpFilterEnvelope.attackCurve = 'linear';
        this.hpFilterEnvelope.decayCurve = 'linear';
        this.hpFilterEnvelope.releaseCurve = 'linear';

        this.filter.cutoffParameter.audioParam.input.automationRate = 'a-rate';

        // connect the amp envelopes to the filter
        this.outputCrossFade.connect(this.filter.effectNode);

        this.setupFilterParameters();
    }

    // since the HP filter is the very last thing in the chain, the
    // connect() func for the synth connects the hpFilter to the
    // destination node
    connect(node) {
        if (node !== undefined) {
            this.hpFilter.connect(node);
        }
    }

    // disconnects the synth from the given audio node (i.e. the mix)
    disconnect(node) {
        this.hpFilter.disconnect(node);
    }

    setupFilterParameters() {
        this.filterCutoffParameter = new AbstractAudioParamParameter(
            'Filter Cutoff',
            this.filter.effectNode.frequency, (o, x) => {
                if (isNaN(x)) {
                    return;
                }
                if (this.filterEnvelope) {
                    this.filterEnvelope.frequency = x;
                } 
                if (this.filter) {
                    this.filter.cutoffFrequency = x;
                }
                if (this.oscillators) {
                    this.oscillators.forEach(osc => osc.cutoffFrequency = x);
                }
            },
            {
                min: 0,
                max: 10000,
                unit: 'HZ',
                defaultValue: 10000,
                constantAutomationRate: true,
                documentation: [
                    "Controls the frequency at which filtering works at. ",
                    "Frequencies above this value will be cut."
                ]
            });

        this.registerParameter(
            this.filterCutoffParameter, (voice) => voice.filter.frequency);

        this.filterQParameter = new AbstractAudioParamParameter(
            'Filter Q',
            this.filter.effectNode.Q, (o, x) => this.filter.Q = x,
            {
                min: 0,
                max: 9,
                unit: '',
                defaultValue: 1
            });

        this.registerParameter(
            this.filterQParameter, (voice) => voice.filter.Q);

        this.hpFilterCutoffParameter = new AbstractAudioParamParameter(
            'Filter Cutoff (hi-pass)',
            this.hpFilter.effectNode.frequency, (o, x) => {
                if (isNaN(x)) {
                    return;
                }
                this.hpFilterEnvelope.frequency = x;
                this.hpFilter.cutoffFrequency = x;
            },
            {
                documentation: [
                    "Controls the frequency at which filtering works at. ",
                    "Frequencies below this value will be cut."
                ],
                min: 0,
                max: 10000,
                unit: 'HZ',
                defaultValue: 0,
                constantAutomationRate: true,
            });

        this.registerParameter(
            this.hpFilterCutoffParameter);

        // Filter's Q - can be synced to LFO
        this.hpFilterQParameter = new AbstractAudioParamParameter(
            'Filter Q (hi-pass)',
            this.filter.effectNode.Q, (o, x) => this.hpFilter.Q = x,
            {
                min: 0,
                max: 9,
                unit: '',
                defaultValue: 1
            });

        this.registerParameter(
            this.hpFilterQParameter);

   }
}
